import React, { useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";

const LogoMark = () => (
  <div className="flex items-center gap-2">
    <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#14b85a] text-white text-base font-bold">
      B
    </div>
    <span className="text-[20px] font-extrabold text-[#14b85a]">BUY iT</span>
  </div>
);

const PendingIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9" stroke="#111" strokeWidth="1.6" />
    <path d="M12 7v5l3 2" stroke="#111" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const UserIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="8" r="4" stroke="#111" strokeWidth="1.6" />
    <path d="M5 20c1.8-3 5-5 7-5s5.2 2 7 5" stroke="#111" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const BikeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="6" cy="17" r="3" stroke="#111" strokeWidth="1.6" />
    <circle cx="18" cy="17" r="3" stroke="#111" strokeWidth="1.6" />
    <path d="M9 17h4l3-6h-4l-2-4H6" stroke="#111" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 17l3-6" stroke="#111" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const AllIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="4" width="7" height="7" rx="1" stroke="#111" strokeWidth="1.6" />
    <rect x="13" y="4" width="7" height="7" rx="1" stroke="#111" strokeWidth="1.6" />
    <rect x="4" y="13" width="7" height="7" rx="1" stroke="#111" strokeWidth="1.6" />
    <rect x="13" y="13" width="7" height="7" rx="1" stroke="#111" strokeWidth="1.6" />
  </svg>
);

const MoreIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="5" cy="12" r="2" fill="#111" />
    <circle cx="12" cy="12" r="2" fill="#111" />
    <circle cx="19" cy="12" r="2" fill="#111" />
  </svg>
);

const TopBar = () => (
  <div className="flex items-center justify-between border-b border-gray-200 bg-[#f3f4f4] px-6 py-4">
    <LogoMark />
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#14b85a] text-sm font-semibold text-white">
        J
      </div>
      <div className="text-right">
        <p className="text-sm font-semibold text-[#0b0b0b]">Good Afternoon, Jeff</p>
        <p className="text-xs text-gray-600">Admin</p>
      </div>
    </div>
  </div>
);

const SideNav = () => {
  const items = [
    { label: "Pending", icon: <PendingIcon /> },
    { label: "Approved", icon: <UserIcon /> },
    { label: "Declined", icon: <BikeIcon /> },
    { label: "All", icon: <AllIcon /> },
    { label: "More", icon: <MoreIcon /> },
  ];

  return (
    <nav className="flex w-64 flex-col gap-2 bg-[#f3f4f4] px-4 py-6">
      {items.map((item, index) => {
        const isActive = index === 0;
        return (
          <div
            key={item.label}
            className={`flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-semibold transition ${
              isActive
                ? "bg-white text-[#14b85a] shadow-sm"
                : "text-[#0b0b0b] hover:bg-white"
            }`}
          >
            <span>{item.icon}</span>
            <span>{item.label}</span>
          </div>
        );
      })}
    </nav>
  );
};

const Field = ({ label, value }) => (
  <div className="rounded-lg border border-gray-100 bg-white px-4 py-3 shadow-sm">
    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
    <p className="mt-1 text-sm font-semibold text-[#0b0b0b]">{value}</p>
  </div>
);

const DeclineModal = ({ open, onClose, reason, setReason, onDecline }) => {
  if (!open) return null;
  const canDecline = reason.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div className="relative z-10 w-full max-w-md rounded-xl bg-white p-6 shadow-lg">
        <h2 className="text-lg font-semibold text-[#0b0b0b]">Decline Vendor</h2>
        <p className="mt-2 text-sm text-gray-600">Provide a reason for declining this vendor.</p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          className="mt-4 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-[#0b0b0b] focus:border-[#14b85a] focus:outline-none"
          placeholder="Type reason here..."
        />
        <div className="mt-5 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onDecline}
            disabled={!canDecline}
            className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${
              canDecline ? "bg-red-600 hover:bg-red-700" : "cursor-not-allowed bg-gray-400"
            }`}
          >
            Decline
          </button>
        </div>
      </div>
    </div>
  );
};

const VendorDetailsScreen = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [vendorData, setVendorData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState(null);
  const { id } = useParams();

  useEffect(() => {
    if (!id) return;
    const fetchVendor = async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("adminToken");
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/vendors/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        if (!data.success) {
          throw new Error(data.message || "Failed to load vendor");
        }
        setVendorData(data.data || null);
      } catch (err) {
        setError(err.message);
        setVendorData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchVendor();
  }, [id]);

  const vendor = useMemo(() => {
    if (!vendorData) {
      return {
        fullName: "Debz Cosmetics Store",
        email: "debz@buyit.com",
        phone: "08012345678",
        storeName: "Debz Cosmetics",
        storeCategory: "Beauty",
        storeState: "Delta",
        storeCity: "Abraka",
        storeAddress: "14 Grace Abosede Street",
        accountName: "Debz Cosmetics",
        bankName: "Moniepoint",
        accountNumber: "0123456789",
      };
    }
    return {
      fullName: vendorData.fullName,
      email: vendorData.email,
      phone: vendorData.phoneNumber,
      storeName: vendorData.storeName,
      storeCategory: vendorData.storeCategory,
      storeState: vendorData.state,
      storeCity: vendorData.city,
      storeAddress: vendorData.storeAddress,
      accountName: vendorData.accountName,
      bankName: vendorData.bankName,
      accountNumber: vendorData.accountNumber,
    };
  }, [vendorData]);

  const submitDecline = () => {
    if (!id || !declineReason.trim()) return;
    const declineVendor = async () => {
      try {
        const token = localStorage.getItem("adminToken");
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/vendors/${id}/decline`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ reason: declineReason.trim() }),
        });
        const data = await res.json();
        if (!data.success) {
          throw new Error(data.message || "Failed to decline vendor");
        }
        setVendorData((prev) =>
          prev
            ? {
                ...prev,
                status: "declined",
                declineReason: declineReason.trim(),
              }
            : prev
        );
        setMessage(data.message || "Vendor declined successfully.");
        setSuccess(true);
        setTimeout(() => setMessage(""), 2000);
        setIsModalOpen(false);
        setDeclineReason("");
      } catch (err) {
        setMessage(err.message || "Failed to decline vendor");
        setSuccess(false);
        setTimeout(() => setMessage(""), 2000);
      }
    };
    declineVendor();
  };

  const confirmVendor = () => {
    if (!id) return;
    const approveVendor = async () => {
      try {
        const token = localStorage.getItem("adminToken");
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/vendors/${id}/approve`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        if (!data.success) {
          throw new Error(data.message || "Failed to approve vendor");
        }
        setVendorData((prev) =>
          prev
            ? {
                ...prev,
                status: "approved",
              }
            : prev
        );
        setMessage(data.message || "Vendor approved successfully.");
        setSuccess(true);
        setTimeout(() => setMessage(""), 2000);
      } catch (err) {
        setMessage(err.message || "Failed to approve vendor");
        setSuccess(false);
        setTimeout(() => setMessage(""), 2000);
      }
    };
    approveVendor();
  };

  return (
    <div className="min-h-screen w-full overflow-x-auto bg-[#f3f4f4] text-[#0b0b0b]">
      {message && (
        <div
          className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-lg shadow-lg text-sm font-semibold animate-fadeInOut ${
            success ? "bg-[#19C463] text-white" : "bg-red-500 text-white"
          }`}
          style={{ minWidth: "220px", textAlign: "center", letterSpacing: "0.5px" }}
        >
          {message}
        </div>
      )}
      <div className="min-h-screen min-w-[600px]">
        <TopBar />
        <div className="flex">
          <SideNav />
          <main className="flex-1 px-6 py-6">
            <div className="mb-6">
              <h1 className="text-xl font-semibold">BUY iT Vendor Section</h1>
              <p className="mt-1 text-sm text-gray-600">Vendor details and verification actions.</p>
            </div>

            {loading ? (
              <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-600 shadow-sm">
                Loading vendor...
              </div>
            ) : error ? (
              <div className="rounded-lg bg-white p-8 text-center text-sm text-red-600 shadow-sm">
                {error}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <Field label="Full Name" value={vendor.fullName} />
                <Field label="Email Address" value={vendor.email} />
                <Field label="Phone Number" value={vendor.phone} />
                <Field label="Business/Store Name" value={vendor.storeName} />
                <Field label="Store Category" value={vendor.storeCategory} />
                <Field label="Store State" value={vendor.storeState} />
                <Field label="Store City" value={vendor.storeCity} />
                <Field label="Store Address" value={vendor.storeAddress} />
                <Field label="Account Name" value={vendor.accountName} />
                <Field label="Bank Name" value={vendor.bankName} />
                <Field label="Account Number" value={vendor.accountNumber} />
              </div>
            )}

            <div className="mt-6 flex items-center gap-4">
              <button
                type="button"
                onClick={() => {
                  if (vendorData?.status === "declined") return;
                  setIsModalOpen(true);
                }}
                disabled={vendorData?.status === "declined"}
                className={`rounded-lg px-5 py-2 text-sm font-semibold ${
                  vendorData?.status === "declined"
                    ? "cursor-not-allowed border border-gray-300 text-gray-400"
                    : "border border-red-600 text-red-600 hover:bg-red-50"
                }`}
              >
                Decline Vendor
              </button>
              <button
                type="button"
                onClick={confirmVendor}
                className="rounded-lg bg-[#14b85a] px-5 py-2 text-sm font-semibold text-white hover:bg-[#119e4d]"
              >
                Confirm Vendor
              </button>
            </div>
          </main>
        </div>
      </div>

      <DeclineModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        reason={declineReason}
        setReason={setDeclineReason}
        onDecline={submitDecline}
      />
    </div>
  );
};

export default VendorDetailsScreen;
